import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import CountdownTimer from './countdown-timer';
import OtpInputMahan from '../otp-input';
import { LoadingButton } from '@mui/lab';
import {
    useLoginMutation,
    useLoginVerifyMutation,
} from '../../auth.api';
import {
    clearMarketingReferralCookie,
    getMarketingReferralCookie,
} from 'utils/referral-cookie';

const defaultErrorMessage: string = 'کد تایید باید 4 رقم باشد';

export function VerifyForm(): JSX.Element {
    const navigate = useNavigate();
    const { state: currentStatus } = useLocation();

    const [resendVerifyCode] = useLoginMutation();
    const [loginVerify, { isLoading }] = useLoginVerifyMutation();

    const [phone] =
        useOutletContext<
            [string, React.Dispatch<React.SetStateAction<string>>]
        >();
    const [timer, setTimer] = useState<string>('02:00');
    const [otp, setOtp] = useState<string>('');
    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
    const [serverError, setServerError] = useState<string | undefined>(
        undefined,
    );

    const otpError =
        serverError ??
        (!isFirstTime && otp.length < 4 ? defaultErrorMessage : undefined);

    useEffect(() => {
        if (!phone) navigate('/');
    }, [phone]);

    useEffect(() => {
        isFirstTime && otp?.length === 4 && handleSubmit();
    }, [otp]);

    const handleSubmit = async () => {
        isFirstTime && setIsFirstTime(false);
        if (!isLoading) {
            if (otp?.length === 4) {
                setServerError(undefined);
                try {
                    const referralToken = getMarketingReferralCookie();
                    const result = await loginVerify({
                        phone: phone ? phone : '',
                        code: otp,
                        ...(referralToken
                            ? { referral_token: referralToken }
                            : {}),
                    }).unwrap();

                    if (result?.mode === 'two_factor') {
                        clearMarketingReferralCookie();
                        navigate('/password');
                    } else if (result?.token) {
                        clearMarketingReferralCookie();
                        localStorage.setItem('token', result.token);
                        window.location.href = '/register';
                    }
                } catch (error: any) {
                    const errorMessage = error?.data?.errors?.code?.[0];
                    setServerError(errorMessage);
                }
            }
        }
    };

    const handleChange = (newValue: string) => {
        setOtp(newValue);
    };

    const handleResendCode = async () => {
        setTimer('02:00');
        resendVerifyCode({ phone: phone + '' });
    };

    return (
        <>
            <Typography
                sx={{
                    color: 'text.primary',
                    marginTop: 1,
                    marginBottom: 1,
                    fontSize: { xs: '18px', sm: '20px' },
                }}
                className="font-yekan-heavy"
                variant="h3"
                component="h1"
            >
                {currentStatus?.mode === 'register'
                    ? 'ثبت نام'
                    : ' ورود به آقای پرداخت'}
            </Typography>
            <Alert variant="outlined" sx={{ marginBottom: 3, marginTop: 1 }}>
                <Typography
                    component="h4"
                    color="text.primary"
                    sx={{
                        fontSize: {
                            xs: '13px',
                            sm: '14px',
                        },
                    }}
                >
                    {`پیامک حاوی کد تایید ارسال شده به شماره موبایل ${phone} را وارد کنید.`}
                </Typography>
            </Alert>
            <OtpInputMahan
                value={otp}
                error={otpError}
                onChange={handleChange}
                onSubmit={handleSubmit}
                disabled={isLoading}
            />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1,
                }}
            >
                <Button
                    onClick={() => navigate('/', { state: { phone } })}
                    disabled={isLoading}
                    variant="text"
                    sx={{
                        fontSize: { xs: '13px', sm: '14px' },
                        bgcolor: 'inherit',
                        padding: 0,
                        '&:hover': {
                            bgcolor: 'inherit',
                        },
                        color: 'text.primary',
                    }}
                >
                    ویرایش شماره
                </Button>
                {timer === '00:00' && (
                    <Button
                        onClick={() => handleResendCode()}
                        variant="text"
                        disabled={isLoading}
                        sx={{
                            fontSize: { xs: '13px', sm: '14px' },
                            bgcolor: 'inherit',
                            padding: 0,
                            color: 'text.primary',
                            '&:hover': {
                                bgcolor: 'inherit',
                            },
                        }}
                    >
                        دریافت مجدد کد تایید
                    </Button>
                )}

                {timer !== '00:00' && (
                    <CountdownTimer
                        timer={timer}
                        setTimer={setTimer}
                        timerDurationLabel={'02:00'}
                        timerDuration={120}
                        fontSize={14}
                    />
                )}
            </Box>

            <LoadingButton
                loading={isLoading}
                sx={{ marginTop: 4 }}
                onClick={handleSubmit}
                disabled={(!isFirstTime && otp?.length < 4) || isLoading}
                variant="contained"
                className="font-sans"
            >
                تایید و ادامه
            </LoadingButton>
        </>
    );
}
